import romans from 'romans';

const ASCII_UPPERCASE_A = 65;
const ALPHABET_LENGTH = 26;

/**
 * Converts a 0-based index to roman numerals.
 * @param index The 0-based index to convert.
 * @param uppercase Whether to return uppercase or lowercase numerals.
 */
export function indexToRoman(index: number, uppercase: boolean): string {
  const num = index + 1;
  const result = romans.romanize(num);
  return uppercase ? result : result.toLowerCase();
}

/**
 * Converts a 1-based number to roman numerals.
 * @param num The 1-based number to convert (0 is treated as 1).
 * @param uppercase Whether to return uppercase or lowercase numerals.
 */
export function numberToRoman(num: number, uppercase: boolean): string {
  if (num <= 0) return uppercase ? 'I' : 'i';
  const result = romans.romanize(num);
  return uppercase ? result : result.toLowerCase();
}

/**
 * Converts a 0-based column index to Excel-style letters.
 * A, B, ..., Z, AA, AB, AC, ..., AZ, BA, BB, ...
 * @param index The 0-based column index.
 */
export function indexToColumnLetter(index: number): string {
  let result = '';
  let n = index + 1;
  while (n > 0) {
    n--;
    result = String.fromCharCode((n % ALPHABET_LENGTH) + ASCII_UPPERCASE_A) + result;
    n = Math.floor(n / ALPHABET_LENGTH);
  }
  return result;
}

/**
 * Converts a 0-based index to Word-style repeating letters.
 * A, B, ..., Z, AA, BB, CC, ..., ZZ, AAA, BBB, ...
 * @param index The 0-based index to convert.
 * @param uppercase Whether to return uppercase or lowercase letters.
 */
export function indexToLatin(index: number, uppercase: boolean): string {
  const letterCode = (index % ALPHABET_LENGTH) + ASCII_UPPERCASE_A;
  const repeatCount = Math.floor(index / ALPHABET_LENGTH) + 1;
  const result = String.fromCharCode(letterCode).repeat(repeatCount);
  return uppercase ? result : result.toLowerCase();
}

/**
 * Converts a 1-based number to Word-style repeating letters.
 * A, B, ..., Z, AA, BB, CC, ..., ZZ, AAA, BBB, ...
 * @param num The 1-based number to convert (0 is treated as 1).
 * @param uppercase Whether to return uppercase or lowercase letters.
 */
export function numberToLatin(num: number, uppercase: boolean): string {
  if (num <= 0) return uppercase ? 'A' : 'a';
  return indexToLatin(num - 1, uppercase);
}
